import type { LMSRecord } from '../data/pediatric';

function normalCDF(x: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1 + sign * y);
}

function findLMSRecord(
  ageMonths: number,
  lmsTable: LMSRecord[]
): LMSRecord {
  if (ageMonths <= lmsTable[0].month) {
    return lmsTable[0];
  }
  if (ageMonths >= lmsTable[lmsTable.length - 1].month) {
    return lmsTable[lmsTable.length - 1];
  }

  for (let i = 0; i < lmsTable.length - 1; i++) {
    const low = lmsTable[i];
    const high = lmsTable[i + 1];
    if (ageMonths >= low.month && ageMonths <= high.month) {
      if (ageMonths === low.month) return low;
      if (ageMonths === high.month) return high;

      const fraction = (ageMonths - low.month) / (high.month - low.month);
      return {
        month: ageMonths,
        L: low.L + fraction * (high.L - low.L),
        M: low.M + fraction * (high.M - low.M),
        S: low.S + fraction * (high.S - low.S),
      };
    }
  }

  return lmsTable[lmsTable.length - 1];
}

export function calculateBMI(weightKg: number, heightCm: number): number {
  if (weightKg <= 0 || heightCm <= 0) return 0;
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return Math.round(bmi * 10) / 10;
}

export function calculatePercentile(
  value: number,
  ageMonths: number,
  sex: 'male' | 'female',
  lmsTable: { male: LMSRecord[]; female: LMSRecord[] }
): { percentile: number; zscore: number; category: string } {
  const table = sex === 'male' ? lmsTable.male : lmsTable.female;
  const lms = findLMSRecord(ageMonths, table);

  let zscore: number;
  if (lms.L === 0) {
    zscore = Math.log(value / lms.M) / lms.S;
  } else {
    zscore = (Math.pow(value / lms.M, lms.L) - 1) / (lms.L * lms.S);
  }

  const percentile = normalCDF(zscore) * 100;

  let category: string;
  if (percentile < 3) {
    category = '<3';
  } else if (percentile < 15) {
    category = '3-15';
  } else if (percentile < 85) {
    category = '15-85';
  } else if (percentile < 97) {
    category = '85-97';
  } else {
    category = '>97';
  }

  return {
    percentile: Math.round(percentile * 100) / 100,
    zscore: Math.round(zscore * 100) / 100,
    category,
  };
}
